import {
  Alert,
  Badge,
  Button,
  Card,
  Center,
  Code,
  Container,
  Group,
  Image,
  PinInput,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { IconShieldCheck, IconShieldOff } from "@tabler/icons-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { EmptyState, ErrorState, LoadingState, PageHeader } from "@how/ui";
import type { AuthenticatorAssuranceLevels } from "@supabase/supabase-js";
import { useMyRoles, useTenantSettings } from "./authorization";
import { useSession, useSupabase } from "./session";
import type { AppSupabaseClient } from "./supabase";

export interface Assurance {
  readonly currentLevel: AuthenticatorAssuranceLevels | null;
  readonly nextLevel: AuthenticatorAssuranceLevels | null;
}

/** The session's authenticator assurance level; nextLevel above currentLevel means a challenge is due. */
export function assuranceQuery(supabase: AppSupabaseClient, userId: string) {
  return queryOptions({
    queryKey: ["aal", userId],
    staleTime: 0,
    queryFn: async (): Promise<Assurance> => {
      const { data, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (error) throw error;
      return { currentLevel: data.currentLevel, nextLevel: data.nextLevel };
    },
  });
}

export function useAssurance() {
  const supabase = useSupabase();
  const session = useSession();
  const userId = session.status === "signed_in" ? session.session.user.id : null;
  return useQuery({ ...assuranceQuery(supabase, userId ?? ""), enabled: userId !== null });
}

export interface Factor {
  readonly id: string;
  readonly friendlyName: string;
  readonly status: "verified" | "unverified";
  readonly createdAt: string;
}

/** The caller's TOTP factors; `verified` ones drive the challenge, unverified ones are abandoned enrollments. */
export function factorsQuery(supabase: AppSupabaseClient, userId: string) {
  return queryOptions({
    queryKey: ["mfa-factors", userId],
    staleTime: 0,
    queryFn: async (): Promise<Factor[]> => {
      const { data, error } = await supabase.auth.mfa.listFactors();
      if (error) throw error;
      return data.all
        .filter((f) => f.factor_type === "totp")
        .map((f) => ({
          id: f.id,
          friendlyName: f.friendly_name ?? "Authenticator app",
          status: f.status,
          createdAt: f.created_at,
        }));
    },
  });
}

export function useFactors() {
  const supabase = useSupabase();
  const session = useSession();
  const userId = session.status === "signed_in" ? session.session.user.id : null;
  return useQuery({ ...factorsQuery(supabase, userId ?? ""), enabled: userId !== null });
}

function useInvalidateMfa() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ["aal"] });
    void queryClient.invalidateQueries({ queryKey: ["mfa-factors"] });
    // Authorization answers depend on aal (platform rule, tenant admin rule); refresh them after a level change.
    void queryClient.invalidateQueries({ queryKey: ["authorize"] });
    void queryClient.invalidateQueries({ queryKey: ["platform-role"] });
  };
}

export interface RequireMfaProps {
  /** Whether this user must have a factor at all (platform rule, or tenant admin rule for privileged roles). */
  readonly required: boolean;
  readonly children: ReactNode;
}

/**
 * A verified factor always has to be challenged at aal1 (the database treats aal1 as "no MFA" for the rules).
 * When `required` and no verified factor exists, enrollment is forced before anything else renders.
 */
export function RequireMfa({ required, children }: RequireMfaProps) {
  const assurance = useAssurance();
  const factors = useFactors();
  if (assurance.isPending || factors.isPending)
    return <LoadingState label="Checking your sign-in" />;
  if (assurance.isError || factors.isError) {
    return (
      <ErrorState
        message="We could not check your sign-in security."
        onRetry={() => {
          void assurance.refetch();
          void factors.refetch();
        }}
      />
    );
  }
  const verified = factors.data.filter((f) => f.status === "verified");
  if (
    assurance.data.nextLevel === "aal2" &&
    assurance.data.currentLevel !== "aal2" &&
    verified[0] !== undefined
  ) {
    return <MfaChallengePage factor={verified[0]} />;
  }
  if (required && verified.length === 0) return <MfaEnrollPage forced />;
  return <>{children}</>;
}

/** Second step of sign-in: a six-digit code from the authenticator app. */
export function MfaChallengePage({ factor }: { factor: Factor }) {
  const supabase = useSupabase();
  const invalidate = useInvalidateMfa();
  const [code, setCode] = useState("");
  const verify = useMutation({
    mutationFn: async (value: string) => {
      const challenge = await supabase.auth.mfa.challenge({ factorId: factor.id });
      if (challenge.error) throw challenge.error;
      const result = await supabase.auth.mfa.verify({
        factorId: factor.id,
        challengeId: challenge.data.id,
        code: value,
      });
      if (result.error) throw result.error;
    },
    onSuccess: () => {
      notifications.show({
        color: "teal",
        title: "Verified",
        message: "Thanks, you are signed in.",
      });
      invalidate();
    },
    onError: () => {
      setCode("");
    },
  });
  return (
    <Container size="xs" py="xl">
      <Center mih="60vh">
        <Card w="100%" maw={420} padding="xl">
          <Stack gap="md" align="center">
            <IconShieldCheck size={32} />
            <Title order={3}>Enter your verification code</Title>
            <Text size="sm" c="dimmed" ta="center">
              Open your authenticator app ({factor.friendlyName}) and enter the six-digit code.
            </Text>
            <PinInput
              length={6}
              type="number"
              oneTimeCode
              autoFocus
              value={code}
              onChange={setCode}
              onComplete={(value) => {
                verify.mutate(value);
              }}
              aria-label="Verification code"
              disabled={verify.isPending}
            />
            {verify.isError ? (
              <Alert color="red" variant="light" w="100%">
                That code is not right. Wait for a new code and try again.
              </Alert>
            ) : null}
            <Button
              fullWidth
              loading={verify.isPending}
              disabled={code.length !== 6}
              onClick={() => {
                verify.mutate(code);
              }}
            >
              Verify
            </Button>
            <Button
              variant="subtle"
              color="gray"
              onClick={() => {
                void supabase.auth.signOut();
              }}
            >
              Sign out
            </Button>
          </Stack>
        </Card>
      </Center>
    </Container>
  );
}

interface Enrollment {
  readonly factorId: string;
  readonly qrCode: string;
  readonly secret: string;
}

export interface MfaEnrollPageProps {
  /** Forced enrollment blocks the app; the only way out is enrolling or signing out. */
  readonly forced?: boolean;
  readonly onDone?: () => void;
}

/** Enrolls a TOTP factor: QR code (or the secret), then the first code proves the app is set up. */
export function MfaEnrollPage({ forced = false, onDone }: MfaEnrollPageProps) {
  const supabase = useSupabase();
  const invalidate = useInvalidateMfa();
  const [enrollment, setEnrollment] = useState<Enrollment | null>(null);
  const [enrollError, setEnrollError] = useState<string | null>(null);
  const [code, setCode] = useState("");

  // One enrollment per mount: React StrictMode runs effects twice in development, and Supabase rejects a second
  // factor with the same friendly name. The name carries a unique suffix so an abandoned attempt never blocks a new one.
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const stamp = new Date();
    const friendlyName = `Authenticator ${stamp.toISOString().slice(0, 10)} ${stamp.getTime().toString(36).slice(-4)}`;
    void supabase.auth.mfa.enroll({ factorType: "totp", friendlyName }).then(({ data, error }) => {
      if (error) {
        setEnrollError("We could not start enrollment. Try again in a moment.");
        return;
      }
      setEnrollment({ factorId: data.id, qrCode: data.totp.qr_code, secret: data.totp.secret });
    });
  }, [supabase]);

  const verify = useMutation({
    mutationFn: async (value: string) => {
      if (enrollment === null) throw new Error("no enrollment");
      const challenge = await supabase.auth.mfa.challenge({ factorId: enrollment.factorId });
      if (challenge.error) throw challenge.error;
      const result = await supabase.auth.mfa.verify({
        factorId: enrollment.factorId,
        challengeId: challenge.data.id,
        code: value,
      });
      if (result.error) throw result.error;
    },
    onSuccess: () => {
      notifications.show({
        color: "teal",
        title: "Authenticator added",
        message: "Codes from this app now protect your account.",
      });
      invalidate();
      onDone?.();
    },
    onError: () => {
      setCode("");
    },
  });

  return (
    <Container size="xs" py="xl">
      <Center mih={forced ? "60vh" : undefined}>
        <Card w="100%" maw={460} padding="xl">
          <Stack gap="md">
            <Stack gap={2}>
              <Title order={3}>
                {forced ? "Set up two-step verification" : "Add an authenticator app"}
              </Title>
              <Text size="sm" c="dimmed">
                {forced
                  ? "Your role requires an authenticator app. Scan the code with Google Authenticator, 1Password, Authy, or a similar app, then enter the code it shows."
                  : "Scan the code with your authenticator app, then enter the code it shows."}
              </Text>
            </Stack>
            {enrollError !== null ? (
              <ErrorState message={enrollError} />
            ) : enrollment === null ? (
              <LoadingState label="Preparing your enrollment" />
            ) : (
              <>
                <Center>
                  <Image
                    src={enrollment.qrCode}
                    alt="QR code for your authenticator app"
                    w={200}
                    h={200}
                  />
                </Center>
                <Text size="xs" c="dimmed" ta="center">
                  Cannot scan? Enter this key by hand: <Code>{enrollment.secret}</Code>
                </Text>
                <Center>
                  <PinInput
                    length={6}
                    type="number"
                    oneTimeCode
                    value={code}
                    onChange={setCode}
                    onComplete={(value) => {
                      verify.mutate(value);
                    }}
                    aria-label="Code from your authenticator app"
                    disabled={verify.isPending}
                  />
                </Center>
                {verify.isError ? (
                  <Alert color="red" variant="light">
                    That code did not match. Wait for the next code and try again.
                  </Alert>
                ) : null}
                <Button
                  loading={verify.isPending}
                  disabled={code.length !== 6}
                  onClick={() => {
                    verify.mutate(code);
                  }}
                >
                  Confirm
                </Button>
              </>
            )}
            {forced ? (
              <Button
                variant="subtle"
                color="gray"
                onClick={() => {
                  void supabase.auth.signOut();
                }}
              >
                Sign out
              </Button>
            ) : null}
          </Stack>
        </Card>
      </Center>
    </Container>
  );
}

/** Account security: the caller's authenticator apps, with enrollment and removal. */
export function SecurityPage() {
  const supabase = useSupabase();
  const factors = useFactors();
  const assurance = useAssurance();
  const invalidate = useInvalidateMfa();
  const [adding, setAdding] = useState(false);
  const remove = useMutation({
    mutationFn: async (factorId: string) => {
      const { error } = await supabase.auth.mfa.unenroll({ factorId });
      if (error) throw error;
    },
    onSuccess: () => {
      notifications.show({
        color: "teal",
        title: "Removed",
        message: "The authenticator was removed.",
      });
      invalidate();
    },
    onError: () => {
      notifications.show({
        color: "red",
        title: "Could not remove",
        message:
          "Removing a verified authenticator needs a fresh verification. Sign out, sign in with a code, and try again.",
      });
    },
  });
  const level = assurance.data?.currentLevel ?? null;
  return (
    <>
      <PageHeader
        title="Security"
        description="Two-step verification with an authenticator app protects your account with a code that changes every 30 seconds."
        actions={
          adding ? null : (
            <Button
              onClick={() => {
                setAdding(true);
              }}
            >
              Add authenticator
            </Button>
          )
        }
      />
      {adding ? (
        <MfaEnrollPage
          onDone={() => {
            setAdding(false);
          }}
        />
      ) : null}
      <Card>
        {factors.isPending ? (
          <LoadingState rows={2} />
        ) : factors.isError ? (
          <ErrorState
            message="Could not load your authenticators."
            onRetry={() => {
              void factors.refetch();
            }}
          />
        ) : factors.data.length === 0 ? (
          <EmptyState
            icon={<IconShieldOff size={22} />}
            title="No authenticator app yet"
            description="Add one to require a code at sign-in."
          />
        ) : (
          <Stack gap="sm">
            <Group gap="xs">
              <Text size="sm" c="dimmed">
                This session:
              </Text>
              <Badge variant="light" color={level === "aal2" ? "teal" : "gray"}>
                {level === "aal2" ? "verified with a code" : "password only"}
              </Badge>
            </Group>
            {factors.data.map((f) => (
              <Group key={f.id} justify="space-between" wrap="wrap">
                <Group gap="xs">
                  <IconShieldCheck size={18} />
                  <Text size="sm">{f.friendlyName}</Text>
                  <StatusPill status={f.status} />
                  <Text size="xs" c="dimmed">
                    added {new Date(f.createdAt).toLocaleDateString()}
                  </Text>
                </Group>
                <Button
                  variant="light"
                  color="red"
                  size="xs"
                  loading={remove.isPending}
                  onClick={() => {
                    remove.mutate(f.id);
                  }}
                >
                  Remove
                </Button>
              </Group>
            ))}
          </Stack>
        )}
      </Card>
    </>
  );
}

function StatusPill({ status }: { status: Factor["status"] }) {
  return (
    <Badge variant="light" color={status === "verified" ? "teal" : "yellow"} size="sm">
      {status === "verified" ? "active" : "not finished"}
    </Badge>
  );
}

/**
 * Tenant surfaces: enrollment is forced only for privileged roles in a tenant that requires MFA for administrators
 * (tenant_settings.require_mfa_for_admins); a verified factor is always challenged.
 */
export function RequireTenantMfa({ children }: { children: ReactNode }) {
  const settings = useTenantSettings();
  const roles = useMyRoles();
  if (settings.isPending || roles.isPending) return <LoadingState label="Checking your sign-in" />;
  const required =
    settings.data?.require_mfa_for_admins === true && roles.data?.isPrivileged === true;
  return <RequireMfa required={required}>{children}</RequireMfa>;
}
